import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeftIcon from "../../assets/svgs/leftArrowIcon.svg";
import RightIcon from "../../assets/svgs/rightArrowIcon.svg";
import AcquisitionTab from "../../components/DetailTabsData/AcquisitionTab";
import PortfolioTab from "../../components/DetailTabsData/PortfolioTab";
import PropertyTab from "../../components/DetailTabsData/PropertyTab";
import SmartPlanTab from "../../components/DetailTabsData/SmartPlanTab";
import TaskTab from "../../components/DetailTabsData/TaskTab";
import Loader from "../../components/Loader";
import SearchDropdownList from "../../components/SearchDropdownList";
import { ROUTES } from "../../routes/routeKeys";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import CompanyContactTab from "./components/CompanyContactTab";
import CompanyDetailHeader from "./components/CompanyDetailHeader";
import CompanyUpdateTab from "./components/CompanyUpdateTab";

const CompanyDetails = (filterState) => {
  const { id } = useParams();
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("updates");
  const [companyDetails, setCompanyDetails] = useState();
  const handleNavigation = (companyId) => {
    let idToNavigate = companyId;
    let path = "company";

    if (typeof companyId === 'object' && companyId !== null) {
      idToNavigate = companyId.id;
      if (companyId.owner_info_type) {
        path = companyId.owner_info_type === "company" ? "company" : "contact";
      }
    }

    const routeBase = path === "company" ? ROUTES.COMPANY_DETAILS : ROUTES.CONTACT_DETAILS;
    navigate(`${routeBase.replace(":id", idToNavigate)}?filter=${encodeURIComponent(JSON.stringify(filterState))}`);
  };


  const fetchCompanyDetails = () => {
    setLoading(true);
    const savedQueryParams = localStorage.getItem("queryParams");
    const savedAdvanceFilters = localStorage.getItem("advanceFilterData");

    // Build clean payload for POST body
    const payload = {};

    // 1. Parse all normal filters (search, sort, page, etc.)
    if (savedQueryParams) {
      const params = new URLSearchParams(savedQueryParams);
      params.forEach((value, key) => {
        if (key !== "advance_filters") {
          payload[key] = value;
        }
      });
    }

    // 2. Add advance filters as REAL ARRAY (this is what makes Previous/Next work)
    if (savedAdvanceFilters) {
      try {
        const advanceFilters = JSON.parse(savedAdvanceFilters);
        if (Array.isArray(advanceFilters) && advanceFilters.length > 0) {
          payload.advance_filters = advanceFilters;
        }
      } catch (e) {
        console.warn("Failed to parse advanceFilterData:", e);
      }
    }


    axios
      .post(`${BASE_URL}/company-getbyid/${id}`, payload, config)
      .then((res) => {
        setCompanyDetails(res?.data?.data);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, [id]);

  const queryParams = new URLSearchParams(location.search);
  const searchValue = queryParams.get('search');

  return (
    <BaseLayout>
      <div className="flex justify-between items-center">
        <p role="button" className="green-H head-5 underline" onClick={() => navigate(`${ROUTES.COMPANIES}?search=${searchValue || ''}`)}>
          Back to Companies
        </p>

        <div className="body-S dark-M flex items-center gap-5">
          {companyDetails?.previous_id !== null && (
            <div
              role="button"
              onClick={() => handleNavigation(companyDetails.previous_id)}
              className="flex items-center gap-2"
            >
              <img src={LeftIcon} alt="Previous" />
              Previous Company
            </div>
          )}

          {companyDetails?.next_id !== null && (
            <div
              role="button"
              onClick={() => handleNavigation(companyDetails.next_id)}
              className="flex items-center gap-2"
            >
              Next Company
              <img src={RightIcon} alt="Next" />
            </div>
          )}
        </div>

        <SearchDropdownList from="company" />
      </div>

      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-20 flex justify-center items-center bg-white/40 backdrop-blur-[1px] rounded-xl">
            <Loader />
          </div>
        )}
        <CompanyDetailHeader companyDetails={companyDetails} onSetCallApiAgain={fetchCompanyDetails} />

        <div className="contact-details light-bg-L rounded-xl min-h-[500px]" style={{ paddingLeft: "0px", paddingRight: "0px" }}>
            <div className="flex justify-between header-tabs">
              <p role="button" onClick={() => setActiveTab("updates")} className={`${activeTab === "updates" ? "head-5 dark-H active" : "body-S dark-M "} tab`}>
                Updates
              </p>
              <p role="button" onClick={() => setActiveTab("contacts")} className={`${activeTab === "contacts" ? "head-5 dark-H active" : "body-S dark-M"} tab`}>
                Contacts
              </p>
              <p role="button" onClick={() => setActiveTab("properties")} className={`${activeTab === "properties" ? "head-5 dark-H active" : "body-S dark-M"} tab`}>
                Properties
              </p>
              <p role="button" onClick={() => setActiveTab("portfolio")} className={`${activeTab === "portfolio" ? "head-5 dark-H active" : "body-S dark-M"} tab`}>
                Portfolio
              </p>
              <p role="button" onClick={() => setActiveTab("acquisition")} className={`${activeTab === "acquisition" ? "head-5 dark-H active" : "body-S dark-M"} tab`}>
                Acquisition Criteria
              </p>
              <p role="button" onClick={() => setActiveTab("tasks")} className={`${activeTab === "tasks" ? "head-5 dark-H active" : "body-S dark-M"} tab`}>
                Tasks
              </p>
              <p role="button" onClick={() => setActiveTab("smartPlans")} className={`${activeTab === "smartPlans" ? "head-5 dark-H active" : "body-S dark-M"} tab`}>
                TouchPlans
              </p>

            </div>

            <div className="p-4" key={id}>
              {activeTab === "updates" && <CompanyUpdateTab parentLoading={loading} />}
              {activeTab === "contacts" && <CompanyContactTab />}
              {activeTab === "properties" && <PropertyTab from="company" />}
              {activeTab === "portfolio" && <PortfolioTab from="company" />}
              {activeTab === "acquisition" && <AcquisitionTab from="company" />}
              {activeTab === "tasks" && <TaskTab from="company" data={companyDetails} />}
              {activeTab === "smartPlans" && <SmartPlanTab from="company" />}

            </div>
          </div>
      </div>
    </BaseLayout>
  );
};

export default CompanyDetails;
